/**
 * Apollo hooks for the Activity surface — executed actions, votes,
 * weekly impact. Each query is profile-scoped + accepts an optional
 * `since` cutoff (epoch ms) so callers can request "last 30 days"
 * without paginating.
 *
 * Mirrors graphql_corrective_actions / graphql_suggestions
 * conventions.
 */
import {gql, useQuery} from '@apollo/client';
import type {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  ExecutedAction,
  Vote,
  WeeklyImpact,
} from '../types/generated/graphql';

const EXECUTED_ACTION_FRAGMENT = gql`
  fragment ExecutedActionInfo on ExecutedAction {
    id
    voteId
    profileId
    verb
    outcome
    detail
    occurredAt
    target {
      __typename
      ... on Author {
        id
        name
        thumbnails {
          url
          width
          height
        }
      }
      ... on Video {
        id
        title
        thumbnails {
          url
          width
          height
        }
        author {
          id
          name
        }
      }
    }
    metricsDelta {
      videosRemoved
      videosSurfaced
      candidatesQueued
    }
  }
`;

const VOTE_FRAGMENT = gql`
  fragment VoteInfo on Vote {
    id
    suggestionId
    profileId
    verb
    rationale
    createdAt
    target {
      __typename
      ... on Author {
        id
        name
        thumbnails {
          url
          width
          height
        }
      }
      ... on Video {
        id
        title
        thumbnails {
          url
          width
          height
        }
        author {
          id
          name
        }
      }
    }
  }
`;

const GET_EXECUTED_ACTIONS = gql`
  ${EXECUTED_ACTION_FRAGMENT}
  query GetExecutedActions($profileId: ID!, $since: Float) {
    executedActions(profileId: $profileId, since: $since) {
      ...ExecutedActionInfo
    }
  }
`;

const GET_MY_VOTES = gql`
  ${VOTE_FRAGMENT}
  query GetMyVotes($profileId: ID!, $since: Float) {
    myVotes(profileId: $profileId, since: $since) {
      ...VoteInfo
    }
  }
`;

const GET_WEEKLY_IMPACT = gql`
  query GetWeeklyImpact($profileId: ID!) {
    weeklyImpact(profileId: $profileId) {
      videosRemoved
      videosSurfaced
      candidatesQueued
    }
  }
`;

export function useExecutedActions(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
  since?: number | null,
) {
  return useQuery<
    {executedActions: ExecutedAction[]},
    {profileId: string; since?: number | null}
  >(GET_EXECUTED_ACTIONS, {
    variables: {profileId, since: since ?? null},
    client,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
}

export function useMyVotes(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
  since?: number | null,
) {
  return useQuery<{myVotes: Vote[]}, {profileId: string; since?: number | null}>(
    GET_MY_VOTES,
    {
      variables: {profileId, since: since ?? null},
      client,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    },
  );
}

export function useWeeklyImpact(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
) {
  return useQuery<{weeklyImpact: WeeklyImpact}, {profileId: string}>(
    GET_WEEKLY_IMPACT,
    {
      variables: {profileId},
      client,
      errorPolicy: 'all',
    },
  );
}
